import React from 'react';
import { Badge, Box, Flex, Input, Text, Textarea } from '@chakra-ui/react';
import { Quotes } from 'phosphor-react';
import { ICampoFicha } from '../../../../hooks/useEventoContrato';

/**
 * De onde veio o valor — é o selo que o operador confere. As cores são as do
 * formulário anterior: Receita roxo, orçamento laranja (PDF da OUTRA parte, por
 * onde um dado bancário errado entraria), o que você digitou verde.
 */
export const ORIGEM: Record<string, { texto: string; cor: string }> = {
  texto: { texto: 'do texto colado', cor: 'blue' },
  orcamento: { texto: 'do orçamento', cor: 'orange' },
  cartao_cnpj: { texto: 'do cartão CNPJ', cor: 'teal' },
  receita: { texto: 'da Receita', cor: 'purple' },
  card: { texto: 'do card', cor: 'gray' },
  operador: { texto: 'você preencheu', cor: 'green' },
  calculado: { texto: 'calculado', cor: 'gray' },
  modelo: { texto: 'padrão do modelo', cor: 'gray' },
  // O responsável operacional é sempre o representante (decisão do user, 11/09).
  representante: { texto: 'igual ao representante', cor: 'gray' },
};

interface Props {
  campo: ICampoFicha;
  valor: string;
  /** O operador mexeu neste campo (salvo ou não): o valor agora é dele. */
  editado: boolean;
  /** A IA está lendo as fontes: a leitura nova substitui a ficha. */
  bloqueado: boolean;
  onChange: (valor: string) => void;
}

/**
 * Um campo da ficha: o valor, DE ONDE ele veio e o que o template tinha antes.
 *
 * A borda da esquerda é o estado, de relance: laranja = obrigatório em branco,
 * vermelho = o valor não tem lastro na fonte, verde = pronto. O trecho é a prova:
 * o pedaço exato da fonte de onde a IA tirou o valor, conferido sem IA.
 */
export default function CampoFicha({ campo, valor, editado, bloqueado, onChange }: Props) {
  const vazio = !valor.trim();
  const longo = campo.como === 'prosa' || campo.como === 'composto';
  const origem = ORIGEM[editado ? 'operador' : (campo.fonte ?? '')];
  const pendente = vazio && campo.obrigatorio;
  const naoConfere = !editado && !vazio && campo.conferido === false;
  let borda = 'green.200';
  if (pendente) borda = 'orange.300';
  else if (naoConfere) borda = 'red.300';
  else if (vazio) borda = 'gray.200';

  return (
    <Box
      id={`campo-${campo.papel}`}
      pl={3}
      borderLeft="3px solid"
      borderColor={borda}
      gridColumn={longo ? { md: '1 / -1' } : undefined}
    >
      <Flex align="center" gap={1.5} mb={1} flexWrap="wrap">
        <Text as="label" htmlFor={campo.papel} fontSize="12px" fontWeight={600} color="gray.700">
          {campo.rotulo}
        </Text>
        {campo.obrigatorio && (
          <Text as="span" color="red.500" aria-hidden>
            *
          </Text>
        )}
        {origem && !vazio && (
          <Badge colorScheme={origem.cor} fontSize="10px" textTransform="none">
            {origem.texto}
          </Badge>
        )}
        {pendente && (
          <Badge colorScheme="orange" fontSize="10px" textTransform="none">
            em branco
          </Badge>
        )}
        {naoConfere && (
          <Badge colorScheme="red" fontSize="10px" textTransform="none">
            não confere com a fonte
          </Badge>
        )}
      </Flex>
      {longo ? (
        <Textarea
          id={campo.papel}
          value={valor}
          fontSize="sm"
          minH="72px"
          isDisabled={bloqueado}
          onChange={e => onChange(e.target.value)}
        />
      ) : (
        <Input
          id={campo.papel}
          size="sm"
          value={valor}
          isDisabled={bloqueado}
          onChange={e => onChange(e.target.value)}
        />
      )}
      {!editado && campo.trecho && (
        <Flex gap={1.5} mt={1.5} color="gray.500" align="flex-start">
          <Box mt="3px" flexShrink={0}>
            <Quotes size={11} weight="fill" />
          </Box>
          <Text fontSize="11px" noOfLines={2} title={campo.trecho}>
            {campo.trecho}
          </Text>
        </Flex>
      )}
      {naoConfere && campo.motivo && (
        <Text fontSize="11px" color="red.600" mt={0.5}>
          {campo.motivo}
        </Text>
      )}
      {campo.antes.length > 0 && (
        <Text
          fontSize="11px"
          color="gray.400"
          mt={0.5}
          noOfLines={1}
          title={campo.antes.join(' · ')}
        >
          no template (fornecedor anterior): <s>{campo.antes[0]}</s>
        </Text>
      )}
    </Box>
  );
}
